import { map } from "lodash";
import React, { useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import {
  DropdownMenu as DropdownMenuBase,
  DropdownMenuContent,
  DropdownMenuContentProps,
  DropdownMenuItem,
  DropdownMenuProps,
  DropdownMenuTrigger,
} from "@/shared/ui/DropDown";
import { Column, Row } from "@/shared/ui/Blocks";
import { Badge } from "@/shared/ui/Badge";
import { Icon, IconName } from "@/shared/ui/Icon";
import { cn } from "@/shared/utils/shadcn";
import { InputField } from "@/shared/ui/InputField";
import { AvatarWithInitials } from "@/shared/ui/Avatar";

export interface EntitySelectLink {
  label: string;
  icon: IconName;
  id: string;
  path?: string;
  onClick?: () => void;
  variant?: "default" | "primary";
  separatorAfter?: boolean;
}

export interface EntityOption {
  name: string;
  icon?: IconName;
  avatar?: string;
  id?: string | number | null;
  RightComponent?: React.ReactNode;
  [key: string]: any;
}

interface EntitySelectDropdownProps extends DropdownMenuProps {
  title?: string;
  children: React.ReactNode;
  side?: DropdownMenuContentProps["side"];
  align?: DropdownMenuContentProps["align"];
  sideOffset?: DropdownMenuContentProps["sideOffset"];
  alignOffset?: DropdownMenuContentProps["sideOffset"];
  searchEnabled?: boolean;
  links?: EntitySelectLink[];
  options: EntityOption[];
  selectedOption?: EntityOption["id"];
  onSelect: (item: EntityOption) => void;
  fullWidth?: boolean;
  manualTrigger?: boolean;
  contentContainerClassName?: string;
  FilterElement?: React.ReactNode;
}

function EntitySelectDropdown(props: EntitySelectDropdownProps) {
  const {
    title,
    children,
    side = "right",
    align = "center",
    sideOffset,
    alignOffset,
    options,
    selectedOption,
    searchEnabled = false,
    FilterElement,
    links,
    fullWidth,
    manualTrigger,
    onSelect,
    onOpenChange = () => {},
    contentContainerClassName,
    ...rest
  } = props;

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const handleSelectEntity = useCallback(
    async (entity: EntityOption) => {
      onOpenChange(false);
      onSelect(entity);
    },
    [onOpenChange, onSelect]
  );

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((entity) =>
      entity.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const entityOptionsList = useMemo(
    () =>
      map(filteredOptions, (entity, i) => {
        const isSelected = entity?.id === selectedOption;
        return (
          <DropdownMenuItem
            key={`EntitySelect-${entity?.id}-option-${i}"}`}
            onClick={() => handleSelectEntity(entity)}
          >
            <Row
              className={cn(
                "gap-3 items-center w-full p-2 rounded-lg hover:bg-grey-100",
                isSelected && "bg-grey-100",
                !isSelected && "px-2 "
              )}
            >
              <AvatarWithInitials
                size="sm"
                className="mr-2"
                initials={entity?.name?.charAt(0)}
                avatar={entity?.avatar}
                FallbackElement={
                  entity?.icon ? (
                    <Icon icon={entity?.icon} size="sm" color="blue" />
                  ) : (
                    <Badge size="xs">
                      <p className="text-sm">{entity?.name?.charAt(0)}</p>
                    </Badge>
                  )
                }
              />
              <Row className="flex-1">
                <p>{entity?.name}</p>
              </Row>
              {entity?.RightComponent}
              {isSelected && <Icon icon="Check" className="" />}
            </Row>
          </DropdownMenuItem>
        );
      }),
    [filteredOptions, selectedOption, handleSelectEntity]
  );

  const EntityLinksSection = useCallback(() => {
    if (!links?.length) return null;

    return (
      <Column className="flex flex-col py-2 gap-2">
        {links.map((link, idx) => (
          <React.Fragment key={link.id}>
            <DropdownMenuItem
              className="py-0"
              onClick={() => {
                onOpenChange(false);
                link.path ? navigate(link.path) : link.onClick?.();
              }}
            >
              <Row className="px-4 py-2 items-center hover:bg-grey-100 rounded-md w-full">
                <Icon
                  icon={link.icon}
                  size="sm"
                  color={link.variant === "primary" ? "blue" : undefined}
                />
                <p
                  className={cn(
                    "ml-3 leading-none",
                    link.variant === "primary" && "text-blue-500"
                  )}
                >
                  {link.label}
                </p>
              </Row>
            </DropdownMenuItem>
            {link.separatorAfter && (
              <div className="h-px bg-grey-200 my-1 w-full" />
            )}
          </React.Fragment>
        ))}
      </Column>
    );
  }, [links, navigate, onOpenChange]);

  return (
    <DropdownMenuBase onOpenChange={onOpenChange} {...rest}>
      <DropdownMenuTrigger manualTrigger={manualTrigger} asChild>
        <div>{children}</div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        matchTriggerWidth={fullWidth}
        className={cn(
          "border-grey-300 bg-white border-solid min-w-[277px]  max-h-[400px] overflow-y-auto scroll-m-0",
          contentContainerClassName
        )}
      >
        <EntityLinksSection />
        {FilterElement}
        <Column className="py-2 w-full">
          {title && (
            <p className="text-gray-500 text-nowrap font-semibold text-ellipsis uppercase text-2xs ml-4 mb-2">
              {title}
            </p>
          )}
          <Row className="gap-2 w-full items-center">
            {searchEnabled && (
              <InputField
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="ghost"
                size="small"
                icon="Search"
                deleteIcon
                onClearValue={() => setSearchTerm("")}
                autoFocus
                wrapperClassName="mx-3 mb-2"
              />
            )}
          </Row>
          {entityOptionsList}
        </Column>
      </DropdownMenuContent>
    </DropdownMenuBase>
  );
}

export { EntitySelectDropdown };
